import React, { useState } from "react";
import { Box, IconButton, TableRow, TableCell, styled } from "@mui/material";
import BlockIcon from '@mui/icons-material/Block';
import MessageIcon from '@mui/icons-material/Message';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PinDropIcon from '@mui/icons-material/PinDrop';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import { getDateTimeString } from "../../../utils/utils";
import { QueuedReport } from "../../../types/models/QueuedReport";
import { QueuedReportAllColumnKeys, QueuedReportListColumns, ReportHistoryAllColumnKeys, ReportHistoryListColumns } from "./QueuedReportListTypes";
import { AppColor, ModerationReportViewMode } from "../../../types/Constants";
import QueuedReportListMenu from "./QueuedReportListMenu";

const StyledTableCell = styled(TableCell)(() => ({
  fontSize: 14,
  padding: 8,
  "&:first-of-type": {
    paddingLeft: 20,
  },
  "&:last-of-type": {
    paddingRight: 20,
  },
}));

interface QueuedReportListItemProps {
  report: QueuedReport;
  viewMode: string;
  openDetail: (report: QueuedReport) => void;
  setRefreshFlag: (flag: boolean) => void;
}

const QueuedReportListItem: React.FC<QueuedReportListItemProps> = ({ report, viewMode, openDetail, setRefreshFlag }) => {

  const [isOpenMenu, setIsOpenMenu] = useState<boolean>(false);

  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const columns = (viewMode === ModerationReportViewMode.Queue) ? QueuedReportAllColumnKeys : ReportHistoryAllColumnKeys;

  const typeIconElement = () => {
    switch (report.report_type) {
      case "Beacon":
        return <PinDropIcon sx={{ color: AppColor.baseOrange, mr: 1 }} />;
      case "User":
        return <PersonIcon sx={{ color: AppColor.baseOrange, mr: 1 }} />;
      case "Message":
        return <MessageIcon sx={{ color: AppColor.baseOrange, mr: 1 }} />;
      default:
        break;
    }
  };

  const actionTypeIconElement = () => {
    switch (report.actioned_type) {
      case "Dismiss":
        return <BlockIcon sx={{ color: AppColor.baseOrange, mr: 1 }} />;
      case "BanUser":
        return <PersonOffIcon sx={{ color: AppColor.baseOrange, mr: 1 }} />;
      case "DeleteBeacon":
        return <LocationOffIcon sx={{ color: AppColor.baseOrange, mr: 1 }} />;
      default:
        break;
    }
  };

  return (
    <TableRow
      key={report.id}
      className="item-hover"
      hover
      sx={{
        "&:hover": {
          backgroundColor: "silver",
          cursor: 'pointer'
        }
      }}>
      {columns.map((value: string) => {
        switch (value) {
          case QueuedReportListColumns.reason:
            return <StyledTableCell key={report.id + " - reason"} align="left" onClick={() => openDetail(report)}>
              <Box display="flex" flexDirection="row" alignItems="center" >
                {report.reason}
              </Box>
            </StyledTableCell>;
          case QueuedReportListColumns.type:
            return <StyledTableCell key={report.id + " - type"} align="left" onClick={() => openDetail(report)}>
              <Box display="flex" flexDirection="row" alignItems="center" >
                {report.report_type}
              </Box>
            </StyledTableCell>;
          case QueuedReportListColumns.created_at:
            return <StyledTableCell key={report.id + " - startDate"} align="left" onClick={() => openDetail(report)}>{getDateTimeString(report.reported_at)}</StyledTableCell>;
          case QueuedReportListColumns.title:
            return <StyledTableCell key={report.id + " - title"} align="left" onClick={() => openDetail(report)}>
              <Box display="flex" flexDirection="row" alignItems="center">
                {typeIconElement()}
                {report.report_title && report.report_id ? report.report_title : "(Deleted)"}
              </Box>
            </StyledTableCell>;
          case QueuedReportListColumns.action:
            return <StyledTableCell key={report.id + " - action"} align="left" width={100} >
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: "center", }}>
                <IconButton
                  ref={anchorRef}
                  onClick={() => { setIsOpenMenu((prevOpen) => !prevOpen); }}>
                  <MoreVertIcon />
                  <QueuedReportListMenu
                    report={report}
                    open={isOpenMenu}
                    anchorElement={anchorRef.current}
                    setIsOpen={setIsOpenMenu}
                    openDetail={() => openDetail(report)}
                    setRefreshFlag={setRefreshFlag}
                  />
                </IconButton>
              </Box>
            </StyledTableCell>;
          case QueuedReportListColumns.owner:
            return <StyledTableCell key={report.id + " - owner"} align="left" onClick={() => openDetail(report)}>
              <Box display="flex" flexDirection="row" alignItems="center" >
                {report.owner_name}
              </Box>
            </StyledTableCell>;
          case ReportHistoryListColumns.action:
            return <StyledTableCell key={report.id + " - action"} align="left" onClick={() => openDetail(report)}>
              <Box display="flex" flexDirection="row" alignItems="center" >
                {actionTypeIconElement()}
                {report.actioned_type}
              </Box>
            </StyledTableCell>;
          case ReportHistoryListColumns.actioned_at:
            return <StyledTableCell key={report.id + " - actioned_at"} align="left" onClick={() => openDetail(report)}>
              <Box display="flex" flexDirection="row" alignItems="center" >
                {getDateTimeString(report.reported_at)}
              </Box>
            </StyledTableCell>;
          case ReportHistoryListColumns.actioned_by:
            return <StyledTableCell key={report.id + " - actioned_by"} align="left" onClick={() => openDetail(report)}>
              <Box display="flex" flexDirection="row" alignItems="center" >
                {report.actioned_user_name}
              </Box>
            </StyledTableCell>;
          default:
            return <Box></Box>;
        }
      })}
    </TableRow>
  );
};

export default QueuedReportListItem;
